import { inject, Injectable } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { catchError, map, Observable, of, shareReplay } from "rxjs";

import { AppConfigService, EAppModule } from "./app-config.service";

/**
 * Feature-level store on top of {@link AppConfigService}. Caches the
 * enabled-modules list for the lifetime of the SPA so feature toggles and the
 * `*underUiIfModuleEnabled` directive don't each fire their own HTTP call.
 *
 * <para>
 * Exposes two surfaces: an observable for declarative streams, and a signal
 * for components / directives that prefer a synchronous read. Both share the
 * same underlying HTTP response. This is where stream operators live — the
 * API service stays pure.
 * </para>
 */

@Injectable({ providedIn: "root" })
export class AppConfigStoreService {
  private readonly appConfigService = inject(AppConfigService);

  /**
   * Hot, multicast stream of the enabled module names. The first subscriber
   * triggers the HTTP call; all later subscribers receive the cached array
   * immediately. Network failures fall back to an empty array — callers always
   * see a safe value.
   */
  readonly enabledModules$: Observable<EAppModule[]> = this.appConfigService.getAppConfig().pipe(
    map(res => res.data?.enabledModules ?? []),
    catchError(() => of<EAppModule[]>([])),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  /**
   * Synchronous snapshot of the enabled modules. Empty array until the first
   * HTTP response lands; updates when it does. Use this from directives /
   * effects that need a sync read without subscribing.
   */
  readonly enabledModules = toSignal(this.enabledModules$, { initialValue: [] });

  /**
   * Convenience helper. Emits `true` once when the given module is loaded at
   * runtime, `false` otherwise. Uses the same shared cache as
   * {@link enabledModules$}.
   */
  isModuleEnabled$(moduleName: EAppModule): Observable<boolean> {
    return this.enabledModules$.pipe(map(list => list.includes(moduleName)));
  }
}
